import { useEffect, useRef } from 'react';
import { apiPrefix } from '../utils/functions';

type SSECallbackMap = Map<string, (event: MessageEvent) => void>;

//  ------------------------------------------------------------
//
//  Component containing functions and hook to handle Server
//  Sent Event connection.
//
//  ------------------------------------------------------------
const SSEHandler = (isSessionValid: boolean | undefined) => {
  const refEventSource = useRef<EventSource | null>(null);
  const refSSECallbacks = useRef<SSECallbackMap>(new Map());
  const refRetryTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  //  ------------------------------------------------------------
  //
  //  Stores callback functions for event types to be handled by
  //  SSE handler.
  //
  //  Note: Callback functions can't be anonymous functions or
  //  functions without static references. EventListeners with
  //  callback functions that have no static references can't
  //  be removed with removeEventListener().
  //
  //  ------------------------------------------------------------
  const addSSECallback = (
    event_type: string,
    callback: (event: MessageEvent) => void,
  ) => {
    // Ensure event type is unique.
    if (refSSECallbacks.current.has(event_type)) return;
    // Store the callback.
    refSSECallbacks.current.set(event_type, callback);
    // Add an event listener for the callback if a connection already exists.
    if (!refEventSource.current) return;
    refEventSource.current.addEventListener(event_type, callback);
  };

  //  ------------------------------------------------------------
  //
  // Performs cleanup of SSE handler.
  //
  //  ------------------------------------------------------------
  const cleanupSSEHandler = () => {
    // Cancel pending reconnect attempts.
    if (refRetryTimeout.current) {
      clearTimeout(refRetryTimeout.current);
      refRetryTimeout.current = null;
    }
    if (refEventSource.current) {
      // Remove all event listeners.
      refSSECallbacks.current.forEach(
        (callback: (event: MessageEvent) => void, event_type: string) => {
          refEventSource.current?.removeEventListener(event_type, callback);
        },
      );
      // Close the connection.
      refEventSource.current.close();
      refEventSource.current = null;
      console.log('SSE: Closed connection.');
    }
  };

  //  ------------------------------------------------------------
  //
  // Creates a new SSE handler.
  //
  //  ------------------------------------------------------------
  const setupSSEHandler = () => {
    // Don't create SSE connection if one exists or session invalid.
    if (!isSessionValid || refEventSource.current) return;

    const eventSource = new EventSource(apiPrefix() + '/v1/sse_source', {
      withCredentials: true,
    });

    eventSource.onopen = () => {
      console.log('SSE: Connection opened.');
    };

    eventSource.onerror = (err) => {
      console.error(`SSE: Connection lost.\nerror: ${err}.`);
      cleanupSSEHandler();

      // Attempt to re-establish event source connection if disconnected.
      refRetryTimeout.current = setTimeout(() => {
        console.log('SSE: Re-establishing connection...');
        setupSSEHandler();
      }, 5000);
    };

    // Add event listeners for stored callbacks.
    refSSECallbacks.current.forEach(
      (callback: (event: MessageEvent) => void, event_type: string) => {
        eventSource.addEventListener(event_type, callback);
      },
    );

    refEventSource.current = eventSource;
  };

  // Fires functions that perform setup and cleanup of SSE handler based on session
  // validity.
  useEffect(() => {
    setupSSEHandler();
    return () => {
      cleanupSSEHandler();
    };
  }, [isSessionValid]);

  return { addSSECallback };
};

export default SSEHandler;
